import { useState, useCallback } from 'react'
import { channelsAPI } from '../services/api'
import { createLogger } from '../utils/logger'

const logger = createLogger('MessageActions')

/**
 * useMessageActions - Manages message edit/delete operations
 * @idempotent - Separate state management for message actions
 * 
 * @param {string} channelId - Current channel ID
 * @returns {Object} - { editingMessage, setEditingMessage, handleEdit, handleDelete, handleCancelEdit }
 */
export function useMessageActions(channelId) {
  const [editingMessage, setEditingMessage] = useState(null)
  const [editContent, setEditContent] = useState('')

  const startEdit = useCallback((message) => {
    setEditingMessage(message)
    setEditContent(message.content)
    logger.debug('Started editing message', { messageId: message.id })
  }, [])

  const cancelEdit = useCallback(() => {
    setEditingMessage(null)
    setEditContent('')
    logger.debug('Cancelled message edit')
  }, [])

  const handleEdit = useCallback(async (messageId, newContent, onSuccess) => {
    if (!newContent || newContent.trim().length < 10) {
      logger.warn('Edit failed: content too short', { length: newContent?.trim().length })
      return { success: false, error: 'Message too short (min 10 characters)' }
    }

    if (newContent.trim().length > 5000) {
      logger.warn('Edit failed: content too long', { length: newContent.trim().length })
      return { success: false, error: 'Message too long (max 5000 characters)' }
    }

    try {
      await channelsAPI.editMessage(channelId, messageId, newContent.trim())
      
      logger.info('Message edited successfully', { messageId, channelId })
      
      // Update message in UI
      if (onSuccess) {
        onSuccess(messageId, newContent.trim())
      }
      
      cancelEdit()
      return { success: true }
    } catch (error) {
      logger.error('Failed to edit message', error, { messageId, channelId })
      return { success: false, error: error.response?.data?.error || 'Failed to edit message' }
    }
  }, [channelId, cancelEdit])

  const handleDelete = useCallback(async (messageId, onSuccess) => {
    // Confirmation dialog
    if (!window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      logger.debug('Delete cancelled by user', { messageId })
      return { success: false, cancelled: true }
    }

    try {
      await channelsAPI.deleteMessage(channelId, messageId)
      
      logger.info('Message deleted successfully', { messageId, channelId })
      
      // Remove message from UI
      if (onSuccess) {
        onSuccess(messageId)
      }
      
      return { success: true }
    } catch (error) {
      logger.error('Failed to delete message', error, { messageId, channelId })
      return { success: false, error: error.response?.data?.error || 'Failed to delete message' }
    }
  }, [channelId])

  return {
    editingMessage,
    editContent,
    setEditContent,
    startEdit,
    cancelEdit,
    handleEdit,
    handleDelete
  }
}
